import React, { useEffect, useState } from 'react';
import { CalendarDays, ChevronRight, Clock, MapPin, RefreshCw, UserRound } from 'lucide-react';
import { listApprovedSubmissions } from '../lib/api';

const TYPE_LABELS = {
  event: 'Community Event',
  announcement: 'Program Announcement',
  happening: 'Patriots Happening',
};

export const normalizeSubmission = (submission) => {
  const parsed = submission.dateTime ? new Date(submission.dateTime) : null;
  const validDate = parsed && !Number.isNaN(parsed.getTime());
  return {
    ...submission,
    desc: submission.description,
    day: validDate ? parsed.toLocaleDateString(undefined, { weekday: 'short' }).toUpperCase() : 'NEW',
    month: validDate ? parsed.toLocaleDateString(undefined, { month: 'short' }).toUpperCase() : 'UPDATE',
    date: validDate ? String(parsed.getDate()).padStart(2, '0') : '•',
    year: validDate ? parsed.getFullYear() : '',
    time: validDate ? parsed.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }) : 'Schedule to be announced',
    location: submission.location || 'Location to be announced',
    hasSchedule: validDate,
    img: submission.img || submission.imageUrl || '',
  };
};

export const ApprovedEventsFeed = ({ onSelect }) => {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('loading');

  const load = async () => {
    setStatus('loading');
    try {
      setItems(await listApprovedSubmissions());
      setStatus('ready');
    } catch (_error) {
      setStatus('error');
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <section className="py-24 bg-white" data-testid="approved-events-section">
      <div className="max-w-5xl mx-auto px-6">
        <div className="mb-10 max-w-2xl">
          <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-4">From Our Coaches &amp; Board</div>
          <h2 className="font-display text-[#0a1f44] text-4xl md:text-5xl">PATRIOTS HAPPENINGS</h2>
        </div>

        {status === 'loading' && (
          <div className="space-y-4" data-testid="approved-events-loading" aria-live="polite">
            {[0, 1].map((item) => <div key={item} className="h-36 bg-[#0a1f44]/5 animate-pulse" />)}
          </div>
        )}

        {status === 'error' && (
          <div className="border-2 border-[#c8102e]/30 p-8 text-center" data-testid="approved-events-error" role="alert">
            <p className="text-[#0a1f44]/70 mb-4">The latest program updates could not be loaded.</p>
            <button type="button" onClick={load} data-testid="approved-events-retry" className="inline-flex items-center gap-2 bg-[#0a1f44] text-white px-5 py-3 font-heading uppercase tracking-widest text-xs">
              <RefreshCw className="w-4 h-4" /> Try Again
            </button>
          </div>
        )}

        {status === 'ready' && items.length === 0 && (
          <div className="border-2 border-dashed border-[#0a1f44]/15 py-16 px-6 text-center" data-testid="approved-events-empty">
            <CalendarDays className="w-9 h-9 text-[#c8102e] mx-auto mb-3" />
            <p className="font-display text-2xl text-[#0a1f44]">NEW UPDATES COMING SOON</p>
            <p className="text-sm text-[#0a1f44]/60 mt-2">Approved coach and board submissions will appear here.</p>
          </div>
        )}

        {status === 'ready' && items.length > 0 && (
          <div className="space-y-4" data-testid="approved-events-list">
            {items.map((submission) => {
              const event = normalizeSubmission(submission);
              return (
                <button
                  key={submission.id}
                  type="button"
                  onClick={() => onSelect(event)}
                  data-testid={`approved-event-${submission.id}`}
                  className="hover-lift group w-full bg-white border-2 border-[#0a1f44]/10 hover:border-[#c8102e] text-left transition-colors flex flex-col sm:flex-row"
                >
                  <div className="bg-[#0a1f44] group-hover:bg-[#c8102e] transition-colors text-white text-center py-5 px-6 sm:w-36 flex sm:flex-col items-center justify-center gap-2 sm:gap-0">
                    <span className="font-heading uppercase tracking-widest text-[10px] opacity-70">{event.day}</span>
                    <span className="font-display text-4xl leading-none sm:my-1">{event.date}</span>
                    <span className="font-heading uppercase tracking-widest text-[10px]">{event.month}</span>
                  </div>
                  <div className="flex-1 min-w-0 p-6">
                    <div className="font-heading uppercase tracking-[0.24em] text-[10px] text-[#c8102e] mb-2">{TYPE_LABELS[submission.type] || 'Program Update'}</div>
                    <div className="font-display text-2xl text-[#0a1f44] leading-tight">{submission.title.toUpperCase()}</div>
                    {submission.description && <p className="text-sm text-[#0a1f44]/70 mt-2 line-clamp-2">{submission.description}</p>}
                    <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-[#0a1f44]/60 mt-4">
                      <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-[#c8102e]" /> {event.time}</span>
                      <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[#c8102e]" /> {event.location}</span>
                      <span data-testid={`approved-event-author-${submission.id}`} className="inline-flex items-center gap-1.5 sm:ml-auto text-[11px]"><UserRound className="w-3.5 h-3.5 text-[#c8102e]" /> Submitted by {submission.contributorName}</span>
                    </div>
                  </div>
                  <div className="hidden sm:flex items-center px-5 text-[#c8102e]"><ChevronRight className="w-5 h-5" /></div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};